/**
 * What the letter says, in whichever of the three ways suits the writer.
 *
 * Shared by the campaign screen and the template screen, because a letter is a
 * letter: the campaign adds a sender and an audience around it, the template
 * adds a name, and neither should own its own copy of "how a body is written".
 *
 * ── The rule that matters ─────────────────────────────────────────────────
 * `bodyHtml` is what gets SENT. `design` is what the builder re-opens. When a
 * design exists it OWNS the HTML: every edit regenerates it, and the HTML tab
 * goes read-only rather than letting somebody hand-edit output that the next
 * builder keystroke would overwrite without warning. Detaching is offered, is
 * explicit, and keeps the HTML as it stands.
 *
 * The plain-text version is nobody's output. It is auto-filled from the design
 * while it still matches what the design last generated, and left strictly
 * alone the moment somebody has typed in it - a text body is the version a
 * conservative corporate client shows, and silently overwriting a hand-tuned
 * one is worse than leaving it stale.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Code2, FileText, LayoutTemplate, Link2Off, Plus, RefreshCw, Type } from "lucide-react";
import { Button, Input, Tabs, TabsContent, TabsList, TabsTrigger, Textarea } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  EmailBuilder,
  renderEmailHtml,
  renderEmailText,
  UNSUB_TOKEN,
  emptyDesign,
  parseDesign,
  type EmailDesign,
  type PresetLocale,
} from "../../../../emailBuilder";
import { InboxPreview } from "../panels/message/InboxPreview";
import { Field, Note, Panel } from "../shared";
import { TemplatePicker, type AppliedLetter } from "./TemplatePicker";

export interface LetterFields {
  subject: string;
  preheader: string;
  bodyHtml: string;
  bodyText: string;
  design: EmailDesign | null;
  /** Only the campaign screen carries this; the template screen ignores it. */
  templateId?: string | null;
}

export function LetterEditor({
  subject,
  preheader,
  bodyHtml,
  bodyText,
  design,
  locale,
  siteId,
  disabled,
  fromName,
  onChange,
}: {
  subject: string;
  preheader: string;
  bodyHtml: string;
  bodyText: string;
  design: EmailDesign | unknown | null;
  locale: PresetLocale;
  siteId?: string | null;
  disabled?: boolean;
  /** Shown in the inbox row. Absent on a template, which has no sender. */
  fromName?: string;
  onChange: (patch: Partial<LetterFields>) => void;
  /** Kept for callers that want their own "start a design" entry point. */
  onStartDesign?: () => void;
}) {
  const t = useT();
  const [tab, setTab] = useState("design");
  const [picking, setPicking] = useState(false);

  // The stored JSON, read once per identity change. `parseDesign` fills in
  // properties that did not exist when the letter was written, and refuses
  // anything that is not a design - which is the honest answer for a column
  // holding hand-written JSON.
  const parsed = useDesign(design);

  // What the design last generated as text. Anything else in `bodyText` is
  // somebody's own writing and is never overwritten.
  const lastGeneratedText = useRef<string>(parsed ? renderEmailText(parsed) : "");

  const handleDesign = useCallback(
    (next: EmailDesign) => {
      const html = renderEmailHtml(next);
      const text = renderEmailText(next);
      const patch: Partial<LetterFields> = { design: next, bodyHtml: html };
      if (!bodyText.trim() || bodyText === lastGeneratedText.current) patch.bodyText = text;
      lastGeneratedText.current = text;
      onChange(patch);
    },
    [bodyText, onChange],
  );

  function applyTemplate(letter: AppliedLetter) {
    // Applying replaces the body outright. Asking first only when there is
    // something to lose - a confirmation on an empty letter is noise, and noise
    // is what teaches people to click through the one that mattered.
    const hasBody = bodyHtml.trim() !== "" || bodyText.trim() !== "";
    if (hasBody && !window.confirm(t("marketing.letter.applyConfirm"))) return;

    lastGeneratedText.current = letter.bodyText;
    onChange({
      design: letter.design,
      bodyHtml: letter.bodyHtml,
      bodyText: letter.bodyText,
      // Only fill a subject that is empty. Somebody who already wrote one
      // reached for the template to get the body, not to lose the line they
      // spent longest on.
      ...(subject.trim() ? {} : { subject: letter.subject }),
      ...(letter.templateId !== undefined ? { templateId: letter.templateId } : {}),
    });
    setPicking(false);
    setTab("design");
  }

  /**
   * Append the opt-out line to a hand-written letter.
   *
   * Only touches a body that exists: the HTML one is optional, so writing into
   * an empty one would turn "text only" into "both" behind the operator's back.
   */
  function addUnsubscribeLine() {
    const patch: Partial<LetterFields> = {};
    if (!bodyText.includes(UNSUB_TOKEN)) {
      const head = bodyText.trimEnd();
      patch.bodyText = `${head ? `${head}\n\n` : ""}${t("marketing.message.unsubLine")} ${UNSUB_TOKEN}\n`;
    }
    if (bodyHtml.trim() && !bodyHtml.includes(UNSUB_TOKEN)) {
      patch.bodyHtml = `${bodyHtml.trimEnd()}\n<p>${t("marketing.message.unsubLine")} <a href="${UNSUB_TOKEN}">${UNSUB_TOKEN}</a></p>\n`;
    }
    onChange(patch);
  }

  /** Give up the builder and keep the HTML as it stands. Explicit, because the
   *  design cannot be recovered afterwards. */
  function detach() {
    if (!window.confirm(t("marketing.letter.detachConfirm"))) return;
    onChange({ design: null });
    setTab("html");
  }

  const hasUnsub = `${bodyHtml}\n${bodyText}`.includes(UNSUB_TOKEN);
  const textIsStale = parsed !== null && bodyText.trim() !== "" && bodyText !== lastGeneratedText.current;

  return (
    <div className="space-y-4">
      <Panel
        title={t("marketing.message.contentTitle")}
        description={t("marketing.message.contentIntro")}
        icon={<Type className="size-4" />}
        actions={
          !disabled && (
            <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
              <LayoutTemplate className="size-3.5" /> {t("marketing.letter.useTemplate")}
            </Button>
          )
        }
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-3.5">
            <Field label={t("marketing.message.subject")}>
              <Input value={subject} onChange={(e) => onChange({ subject: e.target.value })} disabled={disabled} />
            </Field>
            <Field label={t("marketing.message.preheader")} hint={t("marketing.message.preheaderHint")}>
              <Input value={preheader} onChange={(e) => onChange({ preheader: e.target.value })} disabled={disabled} />
            </Field>
          </div>
          <InboxPreview fromName={fromName} subject={subject} preheader={preheader} />
        </div>
      </Panel>

      {/* A commercial message owes its reader a free and easy way to refuse.
          The fix differs by how the letter is written: with a design it is a
          block from the palette, without one it is a line appended to the
          bodies - so the warning offers whichever applies rather than the same
          sentence in both cases. */}
      {!hasUnsub && (
        <Note tone="warn" icon={<AlertTriangle className="size-4" />}>
          {t("marketing.message.noUnsubscribe", { token: UNSUB_TOKEN })}{" "}
          {parsed ? (
            <span className="text-muted-foreground/80">{t("marketing.letter.addFooterBlock")}</span>
          ) : (
            !disabled && (
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={addUnsubscribeLine}
              >
                {t("marketing.message.addUnsubscribe")}
              </button>
            )
          )}
        </Note>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="design" className="gap-1.5">
            <LayoutTemplate className="size-3.5" /> {t("marketing.letter.tab.design")}
          </TabsTrigger>
          <TabsTrigger value="text" className="gap-1.5">
            <FileText className="size-3.5" /> {t("marketing.letter.tab.text")}
          </TabsTrigger>
          <TabsTrigger value="html" className="gap-1.5">
            <Code2 className="size-3.5" /> {t("marketing.letter.tab.html")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="design" className="mt-4">
          {parsed ? (
            <EmailBuilder design={parsed} onChange={handleDesign} disabled={disabled} siteId={siteId} />
          ) : (
            <StartDesign
              hasHtml={bodyHtml.trim() !== ""}
              disabled={disabled}
              onStart={() => handleDesign(emptyDesign())}
              onPick={() => setPicking(true)}
            />
          )}
        </TabsContent>

        <TabsContent value="text" className="mt-4 space-y-3">
          {textIsStale && (
            <Note tone="info">
              {t("marketing.letter.textDiverged")}{" "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => {
                  if (!parsed) return;
                  const text = renderEmailText(parsed);
                  lastGeneratedText.current = text;
                  onChange({ bodyText: text });
                }}
              >
                {t("marketing.letter.regenerateText")}
              </button>
            </Note>
          )}
          <Field label={t("marketing.message.bodyText")} hint={t("marketing.message.bodyTextHint")}>
            <Textarea
              value={bodyText}
              onChange={(e) => onChange({ bodyText: e.target.value })}
              rows={16}
              className="font-mono text-xs leading-relaxed"
              disabled={disabled}
            />
          </Field>
        </TabsContent>

        <TabsContent value="html" className="mt-4 space-y-3">
          {parsed ? (
            <Note tone="info" icon={<RefreshCw className="size-4" />}>
              {t("marketing.letter.htmlGenerated")}{" "}
              {!disabled && (
                <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={detach}>
                  {t("marketing.letter.detach")}
                </button>
              )}
            </Note>
          ) : null}
          <Field label={t("marketing.message.bodyHtml")} hint={t("marketing.message.bodyHtmlHint")}>
            <Textarea
              value={bodyHtml}
              onChange={(e) => onChange({ bodyHtml: e.target.value })}
              rows={16}
              className="font-mono text-xs leading-relaxed"
              // Read-only rather than hidden: seeing what will be sent is the
              // point of this tab, and hiding it would make the builder opaque.
              readOnly={parsed !== null}
              disabled={disabled}
            />
          </Field>
        </TabsContent>
      </Tabs>

      <TemplatePicker
        open={picking}
        locale={locale}
        onClose={() => setPicking(false)}
        onApply={applyTemplate}
      />
    </div>
  );
}

/** Reads the stored JSON into a design, once per identity change. The parent
 *  holds the object; re-parsing on every render would rebuild it each time and
 *  make the builder believe the letter was replaced. */
function useDesign(raw: EmailDesign | unknown | null): EmailDesign | null {
  const [state, setState] = useState<EmailDesign | null>(() => parseDesign(raw));
  const seen = useRef(raw);
  useEffect(() => {
    if (raw === seen.current) return;
    seen.current = raw;
    setState(parseDesign(raw));
  }, [raw]);
  return state;
}

function StartDesign({
  hasHtml,
  disabled,
  onStart,
  onPick,
}: {
  hasHtml: boolean;
  disabled?: boolean;
  onStart: () => void;
  onPick: () => void;
}) {
  const t = useT();
  return (
    <div className="rounded-2xl border border-dashed border-border px-6 py-12 text-center">
      <p className="text-sm font-medium">{t("marketing.letter.noDesignTitle")}</p>
      <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">
        {t(hasHtml ? "marketing.letter.noDesignHasHtml" : "marketing.letter.noDesignHint")}
      </p>
      {!disabled && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button size="sm" onClick={onPick}>
            <LayoutTemplate className="size-3.5" /> {t("marketing.letter.startFromTemplate")}
          </Button>
          <Button size="sm" variant="outline" onClick={onStart}>
            <Plus className="size-3.5" /> {t("marketing.letter.startBlank")}
          </Button>
        </div>
      )}
      {hasHtml && (
        <p className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
          <Link2Off className="size-3.5" /> {t("marketing.letter.noDesignOverwrite")}
        </p>
      )}
    </div>
  );
}
